import { useFetcher } from '@remix-run/react'
import { MailIcon, TrashIcon } from 'lucide-react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { VisibilityStepsContext } from '~/contexts'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { patchShopSchema } from '~/schemas/shop'
import { Button, Dialog, Input, Alert } from '~/ui'
import { cn } from '~/utils/classnames'

const emailsSupportSchema = patchShopSchema.pick({
  supportEmail: true,
  pendingSupportEmail: true,
})
type EmailsSupportSchema = z.infer<typeof emailsSupportSchema>

type Props = {
  onClose: () => void
  open: boolean
}

export const SupportEmailModal = ({ open, onClose }: Props) => {
  let { t } = useTranslation()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setVisibilityContext] = useContext(VisibilityStepsContext)

  const fetcher = useFetcher()
  const shop = useShop()

  const [removeIsLoading, setRemoveIsLoading] = useState(false)
  const submitIsLoading = useMemo(
    () => fetcher.state != 'idle' && !removeIsLoading,
    [fetcher, removeIsLoading]
  )

  const hasPendingSupportEmail =
    !shop.supportEmail &&
    shop.pendingSupportEmail &&
    shop.pendingSupportEmail?.length > 0

  const currentContext = hasPendingSupportEmail
    ? 'pending_email'
    : shop.supportEmail
      ? 'completed_email'
      : 'email'

  const shopEntityId = hasPendingSupportEmail
    ? 'pendingSupportEmail'
    : 'supportEmail'

  const {
    handleSubmit,
    register,
    getFieldState,
    formState,
    clearErrors,
    setError,
  } = useForm<EmailsSupportSchema>({
    reValidateMode: 'onBlur',
  })

  useEffect(() => {
    if (open && fetcher.state === 'loading') {
      setRemoveIsLoading(false)
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, open])

  const onSubmit = async (data: EmailsSupportSchema) => {
    const emailEntry = data.pendingSupportEmail || data.supportEmail

    const submission = z.string().email().safeParse(emailEntry)
    if (!submission.success) {
      setError(shopEntityId, {
        message: !emailEntry?.length
          ? t('errors.email_is_required')
          : t('errors.email_is_invalid'),
        type: 'required',
      })
      return
    }

    // if there's no support email, create supportEmail
    if (!shop.supportEmail) {
      fetcher.submit(
        { supportEmail: emailEntry!, _intent: 'setShopSupportEmail' },
        { method: 'POST', action: makeShopOwnPath(shop, '/settings?index') }
      )
      return
    }

    // if there's support email, update value
    fetcher.submit(
      { supportEmail: emailEntry! },
      { method: 'PATCH', action: makeShopOwnPath(shop, '/settings?index') }
    )
  }

  const triggerEmailRemoval = useCallback(() => {
    setRemoveIsLoading(true)
    fetcher.submit(
      { supportEmail: '', pendingSupportEmail: '' },
      {
        method: 'PATCH',
        action: makeShopOwnPath(shop, '/settings?index'),
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher])

  const onRemoveSupportEmail = () => {
    if (shop.publishedAt) {
      setVisibilityContext({
        isOpen: true,
        step: 'support_email',
        callback: () => triggerEmailRemoval(),
      })
      return
    }

    triggerEmailRemoval()
  }

  const fieldState = getFieldState(shopEntityId)

  return (
    <Dialog
      open={open}
      onOpenChange={(_open) => {
        if (!_open) onClose()
      }}
    >
      <Dialog.Content>
        <Dialog.Title>
          {t(`components.support_modal.${currentContext}_support_title`)}
        </Dialog.Title>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col items-stretch p-3"
        >
          <>
            <p className="text-foreground/64 mb-5 max-w-[453px] text-xs font-medium">
              {t(
                `components.support_modal.${currentContext}_support_description`
              )}
            </p>

            <div className="flex flex-col items-stretch gap-2">
              <>
                <label className="text-foreground/64 text-xs">
                  {t(
                    `components.support_modal.${currentContext}_support_label`
                  )}
                </label>

                <Input.Group>
                  <Input.Prefix>
                    <MailIcon className="opacity-64" />
                  </Input.Prefix>

                  <Input
                    {...register(shopEntityId)}
                    placeholder={t(
                      `components.support_modal.${currentContext}_support_placeholder`
                    )}
                    onInput={() => clearErrors()}
                    autoComplete="off"
                    defaultValue={shop[shopEntityId] || ''}
                  />
                </Input.Group>
              </>
            </div>

            {fieldState.error?.message && fieldState.isDirty && (
              <Alert
                className="mt-3"
                type="error"
                title={t(fieldState.error?.message)!}
              />
            )}

            <div className="mt-5 flex flex-col items-stretch gap-2 sm:flex-row sm:items-start">
              <Button
                type="submit"
                variant="primary"
                disabled={!formState.isValid}
                isLoading={submitIsLoading}
              >
                <p className={cn(submitIsLoading && 'invisible')}>
                  {shop[shopEntityId] !== null
                    ? t('action.update')
                    : t('action.confirm')}
                </p>
              </Button>

              {shop.supportEmail !== null && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onRemoveSupportEmail}
                  isLoading={removeIsLoading}
                >
                  <TrashIcon />
                  {t('action.remove_email')}
                </Button>
              )}
            </div>
          </>
        </form>
      </Dialog.Content>
    </Dialog>
  )
}
